<template>
    <a :class="{ 'deal-box-wrapper': !isGiftList }" @click="e => goToDeal(e, deal)" target="_blank" :href="dealLink"
        ref="dealbox" class="deal-box-wrapper-container">
        <div class="deal-box-giftlist" v-if="isGiftList">
            <div class="deal-box-number">#{{ index + 1 }}</div>
            <div class="deal-box-giftlist-img-container">
                <div v-if="!isImageLoaded" class="blur-box shimmer"></div>
                <img 
                    v-show="isImageLoaded"
                    :src="deal.imgUrl" 
                    :alt="deal.title"
                    @load="onImageLoad"
                    @error="onImageError"
                    loading="lazy"
                >
                <!-- Always render image to trigger load event, but keep it hidden -->
                <img 
                    v-if="!isImageLoaded"
                    :src="deal.imgUrl"
                    alt=""
                    class="image-preloader"
                    @load="onImageLoad"
                    @error="onImageError"
                >
            </div>
            <div class="deal-box-giftlist-text-container">
                <div class="deal-box-giftlist-title">{{ deal.title }}</div>
                <div class="deal-box-giftlist-desc">{{ deal.description }}</div>
                <div class="deal-box-giftlist-btn"><button>Check it Out</button></div>
            </div>
        </div>

        <!-- Product status indicators -->
        <div class="deal-status-indicators">
            <span v-if="deal.isNew === true" class="deal-indicator deal-indicator-new">NEW</span>
            <span v-if="deal.isPriceChanged === true" class="deal-indicator deal-indicator-price-changed">PRICE CHANGED</span>
        </div>
        <div class="deal-box-img-container" v-if="!isGiftList">
            <a class="deal-image-soldout" v-if="deal.isSoldOut">
                <img :src="`/Assets/img/soldout.png`" />
            </a>
            <div v-if="!isImageLoaded" class="blur-box shimmer"></div>
            <img 
                v-show="isImageLoaded"
                class="deal-box-img" 
                :src="deal.imgUrl"
                :alt="deal.title"
                :class="{ 'up-side-down': settings.purimMode }"
                @load="onImageLoad"
                @error="onImageError"
                loading="lazy"
            >
            <!-- Always render image to trigger load event, but keep it hidden -->
            <img 
                v-if="!isImageLoaded"
                :src="deal.imgUrl"
                alt=""
                class="image-preloader"
                @load="onImageLoad"
                @error="onImageError"
            >
        </div>

        <div class="deal-box-desc" v-if="!isGiftList">
            {{ deal.title }}
            <span v-if="isSimpleView && deal.promoCode">
                <br />
                <div class="share-link-container share-link-container-dealbox">
                    <input id="promoCode" v-model="deal.promoCode" class="promo-code-input promo-code-input-dealbox"
                        readonly />
                    <a v-show="!copied" class="promo-copy-code" @click="copyToClipboard($event)">COPY</a>
                    <a v-show="copied" class="promo-copy-code copied">COPIED</a>
                </div>
            </span>
        </div>
        <div :style="{ 'visibility': isDealNegativeOne || !showPrice ? 'hidden' : 'visible' }" v-if="showPrice">
            <div class="crossed-price"><span v-if='deal.retailPrice'>{{ formatPrice(deal.retailPrice) }}</span></div>
            <div class="deal-price-container">
                <div class="deal-price">{{ formatPrice(deal.dealPrice) }}</div>
                <div class="deal-discount" v-if="discount">-{{ discount.toFixed(0) }}%</div>
            </div>
        </div>
        <div v-if="!isGiftList" class="logo-img-container">
            <DealLogoImage :deal="deal" class="logo-img" />
        </div>
    </a>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import util from '@/helpers/util'
import settings from '@/settings'
import ga from '@/helpers/ga'
import clicky from '@/helpers/clicky'
import DealLogoImage from './DealLogoImage.vue'
import { DealDetails } from '@/classes/DealDetails'
import Cookies from 'js-cookie'
import { globalStore } from '@/stores/globalStore'
const props = defineProps<{
    deal: DealDetails
    index: number
    directLinks: boolean,
    showPrice: boolean
}>()

const route = useRoute()
const router = useRouter()
const dealbox = ref<HTMLElement | null>(null)
const dealLink = ref<string | null>(null)
const showTitle = ref(true)
const showDescription = ref(false)
const isGiftList = ref(false)
const isSimpleView = ref(false)
const copied = ref(false)
const isDealVisible = ref(false)
const isImageLoaded = ref(false)

//SETUP
const $globalStore = globalStore()

//computed discount if there is a deal price and a retail price
const discount = computed(() => {
    return util.getDiscountPercentage(props.deal.dealPrice, props.deal.retailPrice)
})

const isDealNegativeOne = computed(() => {
    return props.deal.dealPrice < 0
})

const formatPrice = (price: number): string => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(price)
}

const trackOutboundLink = (url: string): void => {
    ga.event('click', { 'event_category': 'outbound', 'event_label': url, 'transport_type': 'beacon' })
    clicky.goal('GiftClick')
}

const goToDeal = (event: Event, deal: DealDetails): void => {
    console.log('deal', deal)

    // On gift lists, link directly to the URL instead of opening modal
    if (isGiftList.value) {
        // Track the outbound link
        trackOutboundLink(dealLink.value || deal.dealUrl || '')
        // Don't prevent default - let the anchor navigate naturally
        return
    }

    // For non-gift lists, open the Mini Deal Modal (mobile and desktop)
    // Prevent default anchor behavior to avoid scrolling to top
    event.preventDefault()
    event.stopPropagation()

    // Capture scroll position before opening modal to preserve it
    // The modal's lockBodyScroll will use this position
    const currentScroll = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0
    // Store it temporarily on body so lockBodyScroll can use it if needed
    document.body.setAttribute('data-preserved-scroll', currentScroll.toString())
    $globalStore.setSelectedDeal(deal, props.showPrice)
}

const copyToClipboard = (event: Event): void => {
    event.preventDefault()
    event.stopPropagation()
    const copyText = document.getElementById("promoCode") as HTMLInputElement
    if (copyText) {
        copyText.select()
        document.execCommand("copy")
        copied.value = true
        setTimeout(() => {
            copied.value = false
        }, 2000)
    }
}

onMounted(() => {
    //use jscookie
    const cookie_lastref = Cookies.get('MMD_LastRef')


    // Ensure dealUrl is set from dealURL if needed
    if (!props.deal.dealUrl || props.deal.dealUrl === '') {
        if (props.deal.dealURL && props.deal.dealURL !== '') {
            props.deal.dealUrl = props.deal.dealURL;
        }
    }

    if (settings.siteName === 'fivetowncentral' || (cookie_lastref && cookie_lastref.toLowerCase() === '5townscentral')) {
        if (props.deal.dealUrl) {
            props.deal.dealUrl = util.replaceOrAddUrlParam(props.deal.dealUrl, 'tag', '5townscentral-20')
        }
    }
    else if (settings.siteName === '5tcdeals') {
        if (props.deal.dealUrl) {
            props.deal.dealUrl = util.replaceOrAddUrlParam(props.deal.dealUrl, 'tag', '5townscentral-20')
            props.deal.dealUrl = util.replaceOrAddUrlParam(props.deal.dealUrl, 'sharedid', '5tcdeals')
        }
    }
    else if (settings.siteName === 'momazon' || (cookie_lastref && cookie_lastref.toLowerCase() === 'adir')) {
        if (props.deal.dealUrl) {
            props.deal.dealUrl = util.replaceOrAddUrlParam(props.deal.dealUrl, 'tag', 'adirdeals-20')
        }
    }
    else if (settings.siteName === 'alyssagoldwater' || (cookie_lastref && cookie_lastref.toLowerCase() === 'alyssagoldwater')) {
        if (props.deal.dealUrl) {
            props.deal.dealUrl = util.replaceOrAddUrlParam(props.deal.dealUrl, 'tag', 'agoldwater-20')
        }
    }
    else if (cookie_lastref && cookie_lastref.toLowerCase() === 'mmdfriedman') {
        if (props.deal.dealUrl) {
            props.deal.dealUrl = util.replaceOrAddUrlParam(props.deal.dealUrl, 'tag', 'mmdfriedman-20')
            props.deal.dealUrl = util.replaceOrAddUrlParam(props.deal.dealUrl, 'sharedid', 'moshe')
        }
    }
    else if (cookie_lastref && cookie_lastref.toLowerCase() === 'klugman') {
        if (props.deal.dealUrl) {
            props.deal.dealUrl = util.replaceOrAddUrlParam(props.deal.dealUrl, 'tag', '18ventures-20')
            props.deal.dealUrl = util.replaceOrAddUrlParam(props.deal.dealUrl, 'sharedid', 'klugman')
        }
    }
    else if (cookie_lastref && cookie_lastref.toLowerCase() === 'dovidklugman') {
        if (props.deal.dealUrl) {
            props.deal.dealUrl = util.replaceOrAddUrlParam(props.deal.dealUrl, 'tag', 'mmdbraum-20')
            props.deal.dealUrl = util.replaceOrAddUrlParam(props.deal.dealUrl, 'sharedid', 'dovidklugman')
        }
    }
    else if (cookie_lastref && cookie_lastref.toLowerCase() === 'braum') {
        if (props.deal.dealUrl) {
            props.deal.dealUrl = util.replaceOrAddUrlParam(props.deal.dealUrl, 'tag', 'mmdbraum-20')
            props.deal.dealUrl = util.replaceOrAddUrlParam(props.deal.dealUrl, 'sharedid', 'braum')
        }
    }
    else if (cookie_lastref && cookie_lastref.toLowerCase() === 'tempelberg') {
        if (props.deal.dealUrl) {
            props.deal.dealUrl = util.replaceOrAddUrlParam(props.deal.dealUrl, 'tag', 'tempelberg-20')
            props.deal.dealUrl = util.replaceOrAddUrlParam(props.deal.dealUrl, 'sharedid', 'tempelberg')
        }
    }
    // Embed-widget partner attribution: clicks that came in via dealswidget.com plugin
    // arrive here with cookie MMD_LastRef=partner_<partnerId>. We use the master
    // mommydeals0a-20 Amazon tag and pass the per-partner id as ascsubtag so Amazon
    // Associates reporting groups conversions by partner.
    else if (cookie_lastref && cookie_lastref.toLowerCase().startsWith('partner_')) {
        if (props.deal.dealUrl && props.deal.dealProvider === 'Amazon') {
            props.deal.dealUrl = util.replaceOrAddUrlParam(props.deal.dealUrl, 'tag', 'mommydeals0a-20')
            props.deal.dealUrl = util.replaceOrAddUrlParam(props.deal.dealUrl, 'ascsubtag', cookie_lastref)
        }
        else if (props.deal.dealUrl && props.deal.dealProvider === 'Walmart') {
            props.deal.dealUrl = util.replaceOrAddUrlParam(props.deal.dealUrl, 'sharedid', cookie_lastref)
        }
    }
    else if (settings.siteName === 'mommydeals' && props.deal.dealProvider === 'Amazon') {
        if (props.deal.dealUrl) {
            props.deal.dealUrl = util.replaceOrAddUrlParam(props.deal.dealUrl, 'tag', 'mommydeals0a-20')
        }
    }
    else if (settings.siteName === 'mommydeals' && props.deal.dealProvider === 'Walmart') {
        if (props.deal.dealUrl) {
            props.deal.dealUrl = util.replaceOrAddUrlParam(props.deal.dealUrl, 'sharedid', 'mommydeals')
        }
    }
    else if (settings.siteName === 'petpetdeals' && props.deal.dealProvider === 'Amazon') {
        if (props.deal.dealUrl) {
            props.deal.dealUrl = util.replaceOrAddUrlParam(props.deal.dealUrl, 'tag', 'petpetdeals-20')
        }
    }
    else if (settings.siteName === 'petpetdeals' && props.deal.dealProvider === 'Walmart') {
        if (props.deal.dealUrl) {
            props.deal.dealUrl = util.replaceOrAddUrlParam(props.deal.dealUrl, 'sharedid', 'petpetdeals')
        }
    }
    else if (settings.siteName === 'chaideals' && props.deal.dealProvider === 'Amazon') {
        if (props.deal.dealUrl) {
            props.deal.dealUrl = util.replaceOrAddUrlParam(props.deal.dealUrl, 'tag', 'chaideals-20')
        }
    }
    else if (settings.siteName === 'chaideals' && props.deal.dealProvider === 'Walmart') {
        if (props.deal.dealUrl) {
            props.deal.dealUrl = util.replaceOrAddUrlParam(props.deal.dealUrl, 'sharedid', 'chaideals')
        }
    }

    if (route.name === 'giftideas' || route.name === 'giftlist' || route.name === 'giftlist2') {
        isGiftList.value = true
        
        // Check if deal has a search query for gift lists
        if (props.deal.searchQuery && props.deal.searchQuery.trim() !== '') {
            // Generate Amazon search URL with affiliate tag
            const affiliateInfo = util.getAffiliateParams(settings.siteName)
            const encodedQuery = encodeURIComponent(props.deal.searchQuery.trim())
            dealLink.value = `https://www.amazon.com/s?k=${encodedQuery}&tag=${affiliateInfo.amazonTag}`
        } else {
            // Use the deal URL if no search query
            dealLink.value = props.deal.dealUrl
        }
    }
    else {
        isGiftList.value = false


        if (props.directLinks) {
            dealLink.value = props.deal.dealUrl;
        }
        else if (route.query.direct) {
            isSimpleView.value = true
            dealLink.value = props.deal.dealUrl;
        }
        else {
            isSimpleView.value = false
            dealLink.value = '/Deal/' + props.deal.dealId + '/' + props.deal.prettyTitle
        }
    }

    if (dealbox.value) {
        util.doSomethingOnceVisible(dealbox.value, () => { isDealVisible.value = true })
    }
})

const onImageLoad = () => {
    isImageLoaded.value = true
}

const onImageError = () => {
    // Keep shimmer visible if image fails to load
    isImageLoaded.value = false
}
</script>

<style scoped>
.deal-box-desc {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    /* Limit to 3 lines */
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.deal-box-img-container {
    min-height: 264px;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    position: relative;
    padding-top: 10px;
}

.deal-status-indicators {
    position: absolute;
    top: 10px;
    left: 10px;
    z-index: 10;
    display: flex;
    flex-direction: column;
    gap: 5px;
    pointer-events: none; /* Allow clicks to pass through to image */
    width: auto;
    min-width: 0;
}

.deal-indicator {
    display: inline-block;
    padding: 4px 8px;
    font-size: 11px;
    font-weight: bold;
    text-transform: uppercase;
    border-radius: 3px;
    color: white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    letter-spacing: 0.5px;
    white-space: nowrap;
    width: fit-content;
}

.deal-indicator-new {
    background-color: #28a745;
}

.deal-indicator-price-changed {
    background-color: #ffc107;
    color: #000;
}

.deal-box-img {
    max-height: 264px;
    max-width: 75%;
    height: auto;
    width: auto;
}

.blur-box {
    position: absolute;
    width: 75%;
    max-width: 75%;
    height: 264px;
    max-height: 264px;
}

@media all and (max-width: 768px) {
    .deal-box-img-container {
        min-height: auto;
        padding-top: 5px;
        padding-bottom: 5px;
    }
    
    .deal-status-indicators {
        top: 5px;
        left: 5px;
    }
    
    .deal-box-img {
        max-width: 90%;
    }
    
    .blur-box {
        width: 90%;
        max-width: 90%;
        height: auto;
        min-height: 200px;
    }
}

.deal-box-giftlist-img-container {
    position: relative;
    min-height: 200px;
    display: flex;
    justify-content: center;
    align-items: center;
}

.deal-box-giftlist-img-container img {
    max-width: 100%;
    height: auto;
}

.image-preloader {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    pointer-events: none;
}

.deal-box-wrapper-container {
    position: relative;
}

</style>
